import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ExternalLink, HeartPulse, ShieldCheck } from "lucide-react";
import { PETITION_URL, PETITION_GOAL } from "../lib/constants";

/**
 * PetitionModal
 *
 * In-site dialog that opens when any "Sign the Petition" CTA is clicked.
 * Users stay on jclawsc.org (no new tab pop-up); the modal previews the
 * petition and, on Continue, navigates to Change.org.
 *
 * Notes:
 * - Change.org blocks iframe embedding (x-frame-options: sameorigin,
 *   CSP frame-ancestors 'self'), so we cannot render the Change.org page
 *   inside our site. This modal is the closest UX to an "embedded" signup:
 *   the user completes the visual funnel on jclawsc.org, then Continue
 *   sends them to Change.org where the actual signature is captured.
 * - Accessible: role="dialog", focus trap on open, ESC closes, restores
 *   focus to the trigger on close.
 */
const PetitionModal = ({ isOpen, onClose, count = null, triggerRef }) => {
    const dialogRef = useRef(null);
    const primaryBtnRef = useRef(null);

    // Body scroll lock + ESC key + return focus
    useEffect(() => {
        if (!isOpen) return;
        const previouslyFocused = document.activeElement;
        document.body.style.overflow = "hidden";

        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);

        // Focus the primary CTA when opened
        const timer = setTimeout(() => primaryBtnRef.current?.focus(), 50);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKey);
            clearTimeout(timer);
            // Restore focus to the element that triggered the modal
            const target = triggerRef?.current || previouslyFocused;
            if (target && typeof target.focus === "function") {
                target.focus();
            }
        };
    }, [isOpen, onClose, triggerRef]);

    const percent = count !== null && count !== undefined
        ? Math.max(0, Math.min(100, (count / PETITION_GOAL) * 100))
        : null;

    const continueToSign = () => {
        // Same-tab navigation keeps everything in one window.
        // (Users can right-click to open in a new tab if preferred.)
        window.location.href = PETITION_URL;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4 md:px-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="petition-modal-title"
                    data-testid="petition-modal"
                >
                    {/* Backdrop */}
                    <motion.button
                        type="button"
                        aria-label="Close dialog"
                        onClick={onClose}
                        className="absolute inset-0 bg-navy-950/85 backdrop-blur-sm cursor-default"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="petition-modal-backdrop"
                    />

                    {/* Dialog panel */}
                    <motion.div
                        ref={dialogRef}
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="relative bg-editorial text-navy-900 shadow-2xl w-full max-w-lg md:max-w-xl overflow-hidden"
                    >
                        {/* Close button */}
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            data-testid="petition-modal-close"
                            className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full text-navy-900/70 hover:text-navy-900 hover:bg-navy-900/5 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Navy header strip */}
                        <div className="bg-navy-900 text-editorial px-6 md:px-8 py-6 relative overflow-hidden">
                            <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
                            <div className="relative flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-coral">
                                    <HeartPulse className="w-5 h-5 text-white" />
                                </span>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold">Petition · Every Second Matters</p>
                                    <h2 id="petition-modal-title" className="font-serif text-2xl md:text-3xl leading-tight">
                                        Sign the Petition
                                    </h2>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 md:px-8 py-6 md:py-8">
                            <p className="text-navy-800/85 leading-relaxed">
                                Support the <strong>Hotel Cardiac Emergency Preparedness Act</strong> — require AEDs and CPR-trained staff in hotels across the country. Your signature helps show the growing demand for lifesaving preparedness where families travel.
                            </p>

                            {/* Progress preview */}
                            {count !== null && count !== undefined && (
                                <div className="mt-6 bg-editorial-muted border border-editorial-border p-4 md:p-5">
                                    <div className="flex items-baseline justify-between mb-2">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.22em] text-navy-700/70">Current Signatures</p>
                                            <p className="font-serif text-3xl md:text-4xl text-navy-900 leading-none tabular-nums mt-1" data-testid="modal-count">
                                                {count.toLocaleString("en-US")}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] uppercase tracking-[0.22em] text-navy-700/70">Goal</p>
                                            <p className="font-serif text-2xl text-navy-900/60 leading-none tabular-nums mt-1">
                                                {PETITION_GOAL.toLocaleString("en-US")}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="relative h-2 w-full bg-white border border-editorial-border overflow-hidden mt-3">
                                        <motion.div
                                            className="absolute inset-y-0 left-0 bg-coral"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percent}%` }}
                                            transition={{ duration: 0.9, ease: "easeOut" }}
                                        />
                                    </div>
                                    <p className="mt-2 text-xs text-navy-700/70 uppercase tracking-[0.16em]">
                                        {percent.toFixed(1)}% of goal
                                    </p>
                                </div>
                            )}

                            {/* Trust row */}
                            <div className="mt-6 flex items-start gap-2.5 text-xs text-navy-700/70 leading-relaxed">
                                <ShieldCheck className="w-4 h-4 mt-0.5 text-coral flex-shrink-0" aria-hidden />
                                <p>
                                    Your signature is securely captured on Change.org. JC Law Foundation does not collect or store the personal information you enter there.
                                </p>
                            </div>

                            {/* CTAs */}
                            <div className="mt-7 flex flex-col sm:flex-row gap-3">
                                <button
                                    ref={primaryBtnRef}
                                    type="button"
                                    onClick={continueToSign}
                                    data-testid="petition-modal-continue"
                                    className="group inline-flex items-center justify-center gap-2 flex-1 px-6 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors"
                                >
                                    Continue to Sign
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    data-testid="petition-modal-cancel"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-navy-900/20 text-navy-900 hover:border-navy-900 hover:bg-navy-900/5 transition-colors"
                                >
                                    Not Now
                                </button>
                            </div>

                            {/* Fine print link */}
                            <p className="mt-4 text-xs text-navy-700/60 flex items-center gap-1.5">
                                <ExternalLink className="w-3 h-3" aria-hidden />
                                <span>Signing takes place on Change.org, our official petition partner.</span>
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PetitionModal;
